import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { countryProfile, type PayerInvoiceDto, type PayerInvoiceDueDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { tr } from '@/lib/i18n-core';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, MonthPicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

function previousMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
}

export function usePayerInvoicesRequired(): boolean {
  const { user } = useAuth();
  return (user?.branches ?? []).some((branch) => countryProfile(branch.country ?? null).payerTaxInvoiceRequired === true);
}

export function PayerInvoicesPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(previousMonth());

  const due = useQuery({
    queryKey: ['payer-invoices-due', month],
    queryFn: () => get<PayerInvoiceDueDto[]>(`/insurance/payer-invoices/due${qs({ month })}`),
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
  const issued = useQuery({
    queryKey: ['payer-invoices'],
    queryFn: () => get<{ items: PayerInvoiceDto[]; nextCursor: string | null }>(`/insurance/payer-invoices${qs({ limit: 30 })}`),
  });

  const issue = useMutation({
    mutationFn: (payerId: string) => post<PayerInvoiceDto>('/insurance/payer-invoices', { payerId, month }),
    onSuccess: (invoice) => {
      toast.success(`Tax invoice ${invoice.number} issued`, `${invoice.payerName} · ${invoice.month}`);
      void queryClient.invalidateQueries({ queryKey: ['payer-invoices-due'] });
      void queryClient.invalidateQueries({ queryKey: ['payer-invoices'] });
    },
    onError: (error) => toast.error('Could not issue the tax invoice', describeError(error)),
  });

  const print = useMutation({
    mutationFn: (id: string) => get<{ html: string }>(`/insurance/payer-invoices/${id}/print`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error('Could not print', describeError(error)),
  });

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm">Tax invoices to insurers</CardTitle>
        <p className="text-xs text-muted-foreground">One invoice per insurer per month for its share of the bills, issued by the 15th of the following month. Write-offs after that get a credit note.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <SearchFilterBar
          className="mb-0"
          filters={
            <FilterField label={tr('billing', 'month')}>
              <MonthPicker className="w-44" value={month} onChange={(value) => setMonth(value)} />
            </FilterField>
          }
          onReset={month !== previousMonth() ? () => setMonth(previousMonth()) : undefined}
        />
        <DataTable
          dense
          rows={due.data}
          loading={due.isLoading} refreshing={due.isFetching}
          error={due.error}
          rowKey={(row) => row.payerId}
          empty="No insurer share on bills issued that month."
          columns={[
            { header: 'Insurer', sort: (row) => row.payerName, cell: (row) => row.payerName },
            { header: 'Bills', align: 'right', sort: (row) => row.invoices, cell: (row) => row.invoices },
            { header: 'Before VAT', align: 'right', sort: (row) => row.untaxedMinor, cell: (row) => money(row.untaxedMinor) },
            { header: 'VAT', align: 'right', sort: (row) => row.taxMinor, cell: (row) => money(row.taxMinor) },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                row.issuedNumber ? (
                  <Badge variant="success">{row.issuedNumber}</Badge>
                ) : can('payer_invoice.issue') ? (
                  <span className="flex items-center justify-end gap-2">
                    {row.overdue && <Badge variant="destructive">Due {row.issueBy}</Badge>}
                    <Button size="sm" loading={issue.isPending && issue.variables === row.payerId} onClick={() => issue.mutate(row.payerId)}>
                      Issue
                    </Button>
                  </span>
                ) : (
                  <Badge variant="warning">Not issued</Badge>
                ),
            },
          ]}
        />
        <DataTable
          dense
          rows={issued.data?.items}
          loading={issued.isLoading} refreshing={issued.isFetching}
          error={issued.error}
          rowKey={(row) => row.id}
          empty="Nothing issued yet."
          columns={[
            { header: 'Number', sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
            { header: 'Kind', sort: (row) => row.kind, cell: (row) => (row.kind === 'invoice' ? 'Tax invoice' : `Credit note on ${row.relatedNumber ?? ''} (claim ${row.claimNumber ?? ''})`) },
            { header: 'Insurer', sort: (row) => row.payerName, cell: (row) => row.payerName },
            { header: 'Month', sort: (row) => row.month, cell: (row) => row.month },
            { header: 'Total', align: 'right', sort: (row) => row.untaxedMinor + row.taxMinor, cell: (row) => money(row.untaxedMinor + row.taxMinor) },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <Button size="sm" variant="ghost" aria-label="Print" loading={print.isPending && print.variables === row.id} onClick={() => print.mutate(row.id)}>
                  <Printer className="h-4 w-4" />
                </Button>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
